
import React from "react";
import ReactDOM from "react-dom/client";
import { i18nManager } from "../../../../i18n";
import { useSearchParams } from "react-router-dom";
import { Subscriber, Utils } from "../../../../utils";
import { ConfirmDialog, Elements, SidedContainer, Siren } from "../../../shared";
import { ButtonManageRef, Classname, ObjectHelper, Scheme } from "@rivtn/noseur";
import { ApiResponseData, BaseService, ICurrency, IProfile, CurrencyService, SanitizedResponse } from "../../../../services";

function Currencies() {
    const currencyService = CurrencyService.getInstance();
    const dialogErrorRef = React.useRef<HTMLDivElement>(null);
    const labels = i18nManager.Labels.dashboard.ploutous.others.currencies;
    const [activeCurrency, setActiveCurrency] = React.useState<ICurrency>();
    const [searchParams, setSearchParams] = useSearchParams(window.location.search);
    const [currencies, setCurrencies] = React.useState<ApiResponseData<ICurrency>>({});

    React.useEffect(fetchCurrencies, []);

    return SidedContainer({
        searchParams,
        data: currencies,
        setSearchParams,
        onAction: manageCurrency,
        queryData: fetchCurrencies,
        labels: labels.sided_labels,
        activeData: (activeCurrency ? {
            data: activeCurrency,
            element: (<React.Fragment>
                <div className="header">
                    <i className="fa fa-times" onClick={() => {
                        setActiveCurrency(undefined);
                        Utils.updateSearchParams("active_data_id", undefined, setSearchParams);
                    }} />
                    <span className="name">{activeCurrency?.name}</span>
                    <span className="id" style={{ fontWeight: "bold" }}>{activeCurrency?.external_id}</span>
                    <span className="id">{activeCurrency?.code}</span>
                </div>
                <div className="middle actions" style={{ marginBottom: 0 }}>
                    <span style={{ marginTop: 5 }} onClick={() => manageCurrency(activeCurrency)}><b>{i18nManager.Labels.dashboard.ploutous.created_by}:</b> {activeCurrency?.created_by?.first_name} {activeCurrency?.created_by?.last_name} ({activeCurrency?.created_by?.email})</span>
                    <span style={{ marginTop: 5 }} onClick={() => manageCurrency(activeCurrency)}><b>{i18nManager.Labels.dashboard.ploutous.updated_by}:</b> {activeCurrency?.updated_by?.first_name} {activeCurrency?.updated_by?.last_name} ({activeCurrency?.updated_by?.email})</span>
                    <span style={{ marginTop: 5 }} onClick={() => manageCurrency(activeCurrency)}><b>{i18nManager.Labels.dashboard.ploutous.created_at}:</b> {Utils.formatToFineDate5(activeCurrency?.created_at)}</span>
                    <span style={{ marginTop: 5 }} onClick={() => manageCurrency(activeCurrency)}><b>{i18nManager.Labels.dashboard.ploutous.updated_at}:</b> {Utils.formatToFineDate5(activeCurrency?.updated_at)}</span>
                </div>
                <div className="actions" style={{ overflow: "auto" }}>
                    <span className="link" onClick={() => manageCurrency(activeCurrency)}>{i18nManager.Labels.dashboard.ploutous.edit}</span>
                    {activeCurrency?.status !== "ACTIVE" && (<span className="link" onClick={() => activateDeactivateCurrency(activeCurrency, true)}>{i18nManager.Labels.dashboard.ploutous.activate}</span>)}
                    {activeCurrency?.status === "ACTIVE" && (<span className="link danger" onClick={() => activateDeactivateCurrency(activeCurrency, false)}>{i18nManager.Labels.dashboard.ploutous.deactivate}</span>)}
                    {activeCurrency?.status !== "DELETED" && (<span className="link danger" onClick={() => deleteCurrency(activeCurrency)}>{i18nManager.Labels.dashboard.ploutous.delete}</span>)}
                </div>
            </React.Fragment>)
        } : undefined),
        dataTemplate: (currency: ICurrency) => {
            return (<div className={Classname.build("channel", (activeCurrency?.external_id === currency.external_id ? "active" : null))} onClick={() => {
                setActiveCurrency(currency);
                Utils.updateSearchParams("active_data_id", currency.external_id, setSearchParams);
            }}>
                <div className="id" style={currency.status === "DELETED" ? { textDecoration: "line-through" } : {}}>
                    <i className="fa fa-edit link gray" style={{ marginRight: 10 }} onClick={() => manageCurrency(currency)} />
                    <i className={Classname.build("fa fa-trash link danger", (currency.status === "DELETED" ? "noseur-disabled" : null))} style={{ marginRight: 10 }} onClick={() => deleteCurrency(currency)} />
                    <span className="name">{currency.name} ({currency.sign})</span>
                    <span className="type"> - {currency.code}</span>
                </div>
            </div>);
        }
    });

    function fetchCurrencies() {
        const id = searchParams.get("active_data_id");
        currencyService.queryCurrencies(Utils.normalizeUrlParams(searchParams)).then(({ apiResponse }: SanitizedResponse<ICurrency>) => {
            setCurrencies(apiResponse.data!);
            if (id) for (const item of apiResponse.data?.content!) {
                if (item.external_id === id) {
                    setActiveCurrency(item);
                    break;
                }
            }
        }).catch(BaseService.reportError).finally(() => Subscriber.report(Subscriber.KEYS.QUERY_REQUEST_PROMISE_COMPLETED));
    }

    function manageCurrency(currency?: ICurrency) {
        const payload = ObjectHelper.clone(currency ?? {} as any);
        const basics = labels.dialog.manage.basic[currency ? "update" : "create"];
        const dialog = ConfirmDialog({
            ...basics,
            closable: true,
            rightStyle: { flex: 1.5 },
            confirmLabel: basics.confirm_label,
            style: { minWidth: 400, maxWidth: "80%" },
            content: (<div className="add-new-role">
                {Elements.buildConfirmDialogForm(labels.dialog.manage.form, (name: string, value: string) => {
                    dialogErrorRef.current!.innerText = " ";
                    payload[name] = value;
                    return false;
                }, payload, undefined, { marginTop: 0 })}
                <div ref={dialogErrorRef} className="error"> </div>
            </div>),
            onConfirm: (button?: ButtonManageRef) => {
                let errorMessage;
                dialogErrorRef.current!.innerText = " ";
                const formattedPayload = ObjectHelper.clone(payload);
                if (Object.keys(formattedPayload).length < 3) {
                    errorMessage = labels.dialog.manage.fill_required_fields;
                }
                if (errorMessage) {
                    dialogErrorRef.current!.innerText = errorMessage;
                    return false;
                }
                button?.setLoadingState(true);
                const method = (!!currency ? currencyService.updateCurrency : currencyService.createCurrency).bind(currencyService);
                method(formattedPayload)
                    .then(({ }: SanitizedResponse<ICurrency>) => {
                        Siren.alert(basics.successful);
                        dialog.destroy(fetchCurrencies);
                    }).catch(({ errors }: SanitizedResponse<any>) => {
                        dialogErrorRef.current!.innerText = errors?.join("\n") ?? "";
                    }).finally(() => {
                        button?.setLoadingState(false);
                    });
                return false;
            }
        });
        dialog.show();
    }

    function activateDeactivateCurrency(currency: ICurrency, activate: boolean) {
        currencyService.updateCurrency({ external_id: currency.external_id, status: (activate ? "ACTIVE" : "IN_ACTIVE") }).then(() => {
            Siren.alert(activate ? labels.successfully_activate : labels.successfully_deactivate);
            fetchCurrencies();
        }).catch(BaseService.reportError);
    }

    function deleteCurrency(currency: ICurrency) {
        const dialog = ConfirmDialog({
            closable: true,
            title: labels.dialog.delete.title,
            desc: ObjectHelper.expandStringTemplate(labels.dialog.delete.desc, { name_code: `${currency.name} (${currency.sign})` }, { chop: "$" }),
            confirmScheme: Scheme.DANGER,
            onConfirm: (button?: ButtonManageRef) => {
                button?.setLoadingState(true);
                currencyService.deleteCurrency(currency.external_id).then(() => {
                    Siren.alert(labels.dialog.delete.successful);
                    setActiveCurrency(undefined);
                    dialog.destroy(() => {
                        fetchCurrencies();
                    });
                }).catch(BaseService.reportError).finally(() => button?.setLoadingState(false));
                return false;
            }
        });
        dialog.show();
    }

}

export default Currencies;

